import { createSelector } from '@reduxjs/toolkit';
import { LoadingStatuses } from '@/constants/constants';
import type { RootState } from '@/redux/store';
import { TitleEntity } from '@/redux/slices/slice_types';

export const selectTitleSlice = (state: RootState) => state.title || {};
export const selectTitle = createSelector(selectTitleSlice, (slice) => slice.data);
export const selectTitleId = createSelector(selectTitle, (title) => title?.id);
export const selectTitleLoading = createSelector(
  selectTitleSlice,
  (slice) => slice.status === LoadingStatuses.pending || slice.versionStatus === LoadingStatuses.pending || false
);
export const selectTitleType = createSelector(selectTitle, (title) => title?.entity_type);
export const selectTitlePoster = createSelector(selectTitle, (title) => title?.thumbnail_link);
export const selectTitleIsSeries = createSelector(
  selectTitleType,
  (titleType) => titleType === 'series' || titleType === 'campaign' || titleType === 'compilation' || false
);

export const selectTitleSeasonsObject = createSelector(selectTitleSlice, (slice) => slice.seasons || {});
export const selectActiveTitleId = createSelector(selectTitleSlice, (slice) => slice.activeTitleId);
export const selectActiveTitleLevel = createSelector(selectTitleSlice, (state) => state.activeTitleLevel);
export const selectActiveTitleHierarchy = createSelector(selectTitleSlice, (state) => state.activeTitleHierarchy);
export const selectSelectedTitleId = createSelector(selectTitleSlice, (slice) => slice.selectedTitleId);
export const selectFilteredChildId = createSelector(selectTitleSlice, (slice) => slice.filteredChildId);
export const selectActiveVersion = createSelector(selectTitleSlice, (slice) => slice.activeVersion);
export const selectActiveVersions = createSelector(selectTitleSlice, (slice) => slice.activeVersions || []);
export const selectTitleDataById = createSelector(selectTitleSlice, (slice) => slice.titleDataById || {});

export const selectActiveTitle = createSelector([selectTitleDataById, selectActiveTitleId], (titles, activeTitleId): TitleEntity | undefined =>
  activeTitleId !== null ? titles[activeTitleId] : undefined
);

export const selectFilteredActiveTitle = createSelector(
  [selectTitleDataById, selectActiveTitleId, selectFilteredChildId],
  (titles, activeTitleId, filteredChildId): TitleEntity | undefined => {
    if (activeTitleId === null) return undefined;

    const activeTitle = titles[activeTitleId];
    if (!activeTitle) return undefined;

    if (filteredChildId && activeTitle.children && activeTitle.children.length > 0) {
      const filteredChild = activeTitle.children.find((child) => child.id === filteredChildId);
      return {
        ...activeTitle,
        children: filteredChild ? [filteredChild] : activeTitle.children
      };
    }

    return activeTitle;
  }
);

export const selectVersionsByParentIds = createSelector([selectActiveVersions], (activeVersions) => {
  const versionsByParentId: Record<string, TitleEntity[]> = {};
  activeVersions.forEach((version) => {
    if (!version.parent) return;
    if (!versionsByParentId[version.parent]) {
      versionsByParentId[version.parent] = [];
    }
    versionsByParentId[version.parent].push(version);
  });
  return versionsByParentId;
});

export const selectVersionNamesByParentIds = createSelector([selectActiveVersions], (activeVersions) => {
  const versionNamesByParentId: Record<number, string[]> = {};
  activeVersions.forEach((version) => {
    if (!version.parent) return;
    if (!versionNamesByParentId[version.parent]) {
      versionNamesByParentId[version.parent] = [];
    }
    versionNamesByParentId[version.parent].push(version.name);
  });
  return versionNamesByParentId;
});

export const selectChildrenEntityTypes = createSelector([selectTitle], (title) => {
  const childrenEntityTypes = new Set<string>();
  if (title && title.child_entity_types) {
    (title.child_entity_types as (TitleEntity | string)[]).forEach((child) => {
      if (typeof child === 'string') {
        childrenEntityTypes.add(child);
      } else if (child.hidden === false || child.hidden === 'false') {
        childrenEntityTypes.add(child.entity_type);
      }
    });
  }
  return childrenEntityTypes;
});
